import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import { isEqual } from "es-toolkit/compat";
import { nanoid } from "nanoid";
import React, {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import TextInputBare from "../DataInput/TextInput/TextInput";
import { Popover } from "../Popover";
import { TooltipProps } from "../Tooltip/types";
import { useIsOverflow } from "../utils/hooks/useIsOverflow";
import { useShouldFlash } from "../utils/hooks/useShouldFlash";
import {
  Separator,
  getMappedSelectableOptions,
  getSelectableOptions,
  handleKeyDown,
  handleOptionKeyDownArrowDown,
  handleOptionKeyDownArrowUp,
  moveOptionDown,
  moveOptionUp,
  pinOption,
  searchForOpt,
  sortOptionsByPinned,
} from "./SelectPopover.helpers";
import styles from "./SelectPopover.module.scss";
import {
  SelectPopoverContextType,
  SelectPopoverOptionType,
  SelectPopoverType,
  type SelectPopoverContentProps,
} from "./types";

const cx = classNames.bind(styles);

const SelectPopover = ({ children }: { children: ReactNode }) => {
  return (
    <Popover
      contentClassName={cx("popover-content")}
      contentRender={() => <SelectPopoverContent />}
    >
      {children}
    </Popover>
  );
};

export const SelectPopoverContent = (props: SelectPopoverContentProps) => {
  const {
    id,
    value,
    chooseAllOptions,
    options,
    selectableOptions,
    allowOptionPinning,
    isMulti,
    searchProps,
    search: {
      value: searchValue,
      onChange: searchOnChange,
      options: searchFilteredOptions,
    },
  } = useSelectPopoverContext();

  return (
    <>
      <TextInputBare
        {...searchProps}
        type="search"
        name="search"
        value={searchValue}
        onChange={searchOnChange}
        className={cx("search-input")}
      />
      <ul
        {...props}
        className={cx("options-list", props.className)}
        role="listbox"
        aria-multiselectable={isMulti}
      >
        {isMulti ? (
          <>
            <li
              className={cx("option")}
              role="option"
              aria-label="Select all options"
              tabIndex={0}
              onClick={chooseAllOptions}
              onKeyDown={(evt) =>
                handleKeyDown({
                  evt,
                  actionHandlerMap: {
                    Enter: chooseAllOptions,
                    ArrowUp: () => handleOptionKeyDownArrowUp(evt, 0),
                    ArrowDown: () => handleOptionKeyDownArrowDown(evt, 0),
                  },
                })
              }
            >
              <Checkbox
                name={`button-with-select-${id}-all`}
                label={
                  <>
                    Include All
                    {searchValue &&
                    searchFilteredOptions.length !== options.length ? (
                      <span className={cx("label-flair")}>
                        (Some not visible)
                      </span>
                    ) : null}
                  </>
                }
                value={selectableOptions.length === value.length}
                isIndeterminate={Boolean(
                  Array.isArray(value) &&
                    value.length &&
                    value.length !== selectableOptions.length
                )}
                isFocusable={true}
              />
            </li>
            <li role="presentation">
              <Separator />
            </li>
          </>
        ) : null}
        {allowOptionPinning &&
        searchFilteredOptions.every((opt) => !opt.isPinned) ? (
          <li role="presentation">
            <Separator label="Pinned" />
          </li>
        ) : null}
        {searchFilteredOptions.map((opt, idx, opts) => (
          <SelectPopoverOption
            key={`${opt.label}-${idx}`}
            opt={opt}
            idx={idx}
            siblingOpts={opts}
          />
        ))}
      </ul>
    </>
  );
};

const SelectPopoverOption = ({
  opt,
  isNested,
  idx,
  siblingOpts,
}: {
  opt: SelectPopoverOptionType;
  idx: number;
  siblingOpts: SelectPopoverOptionType[];
  isNested?: boolean;
}) => {
  const { isMulti } = useSelectPopoverContext();

  if (!opt.options) {
    return isMulti ? (
      <SelectPopoverMultiOption
        key={opt.label}
        opt={opt}
        idx={idx}
        siblingOpts={siblingOpts}
        isNested={isNested}
      />
    ) : (
      <SelectPopoverSingleOption
        key={opt.label}
        opt={opt}
        idx={idx}
      />
    );
  } else {
    return (
      <SelectPopoverGroupOption
        opt={opt}
        idx={idx}
        siblingOpts={siblingOpts}
        isNested={isNested}
      />
    );
  }
};

const SelectPopoverGroupOption = ({
  opt: optGroup,
  isNested,
  idx,
  siblingOpts,
}: {
  opt: Extract<SelectPopoverOptionType, { options: SelectPopoverOptionType[] }>;
  idx: number;
  siblingOpts: SelectPopoverOptionType[];
  isNested?: boolean;
}) => {
  const [shouldFlash] = useShouldFlash(idx);
  const {
    id,
    isMulti,
    allowReordering,
    isOptSelected,
    canMoveOptionUp,
    canMoveOptionDown,
    moveOptionUp,
    moveOptionDown,
    allowOptionPinning,
    pinOption,
    chooseAllOptionsOfGroup,
  } = useSelectPopoverContext();
  const [isOverflowing, handleOverflowRef] = useIsOverflow();

  const isSelected = isOptSelected(optGroup);

  return (
    <ul className={cx("options-list", "grouped")}>
      <OptionWithTooltipWhenOverflowing
        isOverflowing={isOverflowing}
        tooltipProps={{ content: optGroup.label }}
      >
        <li
          className={cx(
            "option",
            "group-label",
            isSelected && "active",
            shouldFlash && "flash",
            !shouldFlash && "flash-enabled"
          )}
          role={isMulti ? "option" : "listitem"}
          aria-label={
            isMulti
              ? `Select option group ${optGroup.label}`
              : `Option group ${optGroup.label}`
          }
          tabIndex={isMulti ? 0 : -1}
          {...(isMulti
            ? {
                "aria-selected": isSelected,
                onClick: () => chooseAllOptionsOfGroup(optGroup),
                onKeyDown: (evt) =>
                  handleKeyDown({
                    evt,
                    actionHandlerMap: {
                      Enter: () => chooseAllOptionsOfGroup(optGroup),
                      ArrowUp: () => handleOptionKeyDownArrowUp(evt, idx + 1),
                      ArrowDown: () =>
                        handleOptionKeyDownArrowDown(evt, idx + 1),
                    },
                  }),
              }
            : {})}
        >
          {isMulti ? (
            <Checkbox
              name={`button-with-select-${id}-${optGroup.value}`}
              label={optGroup.label}
              value={isSelected}
              isFocusable={true}
              ref={handleOverflowRef}
              className={cx("option-checkbox", "grouped")}
            />
          ) : (
            <p style={{ margin: 0 }}>
              {optGroup.slotLeft?.({
                iconProps: { size: 16, style: { marginRight: 4 } },
              }) ?? null}
              <span className={cx("label-truncation-wrapper")}>
                {optGroup.label}
              </span>
              {optGroup.slotRight?.({
                iconProps: { size: 16, style: { marginLeft: 4 } },
              }) ?? null}
            </p>
          )}
          {isMulti ? (
            <div className={cx("option-action-group")}>
              {allowOptionPinning && !isNested ? (
                <Button
                  title={!optGroup.isPinned ? "Pin" : "Un-pin"}
                  icon={
                    !optGroup.isPinned
                      ? "push-pin-simple"
                      : "push-pin-simple-slash"
                  }
                  iconProps={{
                    size: 16,
                    weight: "bold",
                  }}
                  size="xx-small"
                  onClick={(evt) => {
                    evt.stopPropagation();

                    // if user provided pin handling
                    pinOption(optGroup);
                  }}
                />
              ) : null}
              {allowReordering ? (
                <>
                  <Button
                    title="Move down"
                    icon="arrow-circle-down"
                    iconProps={{ size: 16, weight: "bold" }}
                    size="xx-small"
                    disabled={!canMoveOptionDown(idx, siblingOpts)}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      moveOptionDown(optGroup);
                    }}
                  />
                  <Button
                    title="Move up"
                    icon="arrow-circle-up"
                    iconProps={{ size: 16, weight: "bold" }}
                    size="xx-small"
                    disabled={!canMoveOptionUp(idx, siblingOpts)}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      moveOptionUp(optGroup);
                    }}
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </li>
      </OptionWithTooltipWhenOverflowing>
      <ul
        className={cx("options-list", "nested")}
        role="listbox"
      >
        {optGroup.options.map((opt, idx, opts) => (
          <SelectPopoverOption
            key={opt.label}
            opt={opt}
            siblingOpts={opts}
            isNested
            idx={idx}
          />
        ))}
      </ul>
      {allowOptionPinning &&
      optGroup.isPinned &&
      siblingOpts[idx + 1] &&
      !siblingOpts[idx + 1].isPinned ? (
        <li role="presentation">
          <Separator label="Pinned" />
        </li>
      ) : !isNested ? (
        <li role="presentation">
          <Separator />
        </li>
      ) : null}
    </ul>
  );
};

const SelectPopoverMultiOption = ({
  opt,
  isNested,
  idx,
  siblingOpts,
}: {
  opt: Extract<SelectPopoverOptionType, { options: undefined }>;
  idx: number;
  siblingOpts: SelectPopoverOptionType[];
  isNested?: boolean;
}) => {
  const {
    id,
    setValue,
    allowReordering,
    isOptSelected,
    canMoveOptionUp,
    canMoveOptionDown,
    moveOptionUp,
    moveOptionDown,
    allowOptionPinning,
    pinOption,
  } = useSelectPopoverContext();
  const [shouldFlash] = useShouldFlash(idx);
  const [isOverflowing, handleOverflowRef] = useIsOverflow();

  const isSelected = isOptSelected(opt);

  return (
    <>
      <OptionWithTooltipWhenOverflowing
        isOverflowing={isOverflowing}
        tooltipProps={{ content: opt.label }}
      >
        <li
          key={opt.value}
          className={cx(
            "option",
            isSelected && "active",
            shouldFlash && "flash",
            !shouldFlash && "flash-enabled"
          )}
          role="option"
          aria-label={`Select option ${opt.label}`}
          aria-selected={isSelected}
          tabIndex={0}
          onClick={() => setValue(opt.value)}
          onKeyDown={(evt) =>
            handleKeyDown({
              evt,
              actionHandlerMap: {
                Enter: () => setValue(opt.value),
                ArrowUp: () => handleOptionKeyDownArrowUp(evt, idx + 1),
                ArrowDown: () => handleOptionKeyDownArrowDown(evt, idx + 1),
              },
            })
          }
        >
          <Checkbox
            name={`button-with-select-${id}-${opt.value}`}
            label={
              <>
                {opt.slotLeft?.({
                  iconProps: { size: 16, style: { marginRight: 4 } },
                }) ?? null}
                {opt.label}
                {opt.slotRight?.({
                  iconProps: { size: 16, style: { marginLeft: 4 } },
                }) ?? null}
              </>
            }
            value={isSelected}
            isFocusable={true}
            ref={handleOverflowRef}
            className={cx("option-checkbox")}
          />
          <div className={cx("option-action-group")}>
            {allowOptionPinning && !isNested ? (
              <Button
                title={!opt.isPinned ? "Pin" : "Un-pin"}
                icon={
                  !opt.isPinned ? "push-pin-simple" : "push-pin-simple-slash"
                }
                iconProps={{
                  size: 16,
                  weight: "bold",
                }}
                size="xx-small"
                onClick={(evt) => {
                  evt.stopPropagation();

                  // if user provided pin handling
                  pinOption(opt);
                }}
              />
            ) : null}
            {allowReordering ? (
              <>
                <Button
                  title="Move down"
                  icon="arrow-circle-down"
                  iconProps={{ size: 16, weight: "bold" }}
                  size="xx-small"
                  disabled={!canMoveOptionDown(idx, siblingOpts)}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    moveOptionDown(opt);
                  }}
                />
                <Button
                  title="Move up"
                  icon="arrow-circle-up"
                  iconProps={{ size: 16, weight: "bold" }}
                  size="xx-small"
                  disabled={!canMoveOptionUp(idx, siblingOpts)}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    moveOptionUp(opt);
                  }}
                />
              </>
            ) : null}
          </div>
        </li>
      </OptionWithTooltipWhenOverflowing>
      {!isNested &&
      allowOptionPinning &&
      opt.isPinned &&
      siblingOpts[idx + 1] &&
      !siblingOpts[idx + 1].isPinned ? (
        <li role="presentation">
          <Separator label="Pinned" />
        </li>
      ) : null}
    </>
  );
};

const SelectPopoverSingleOption = ({
  opt,
  idx,
}: {
  opt: Extract<SelectPopoverOptionType, { options: undefined }>;
  idx: number;
}) => {
  const { setValue, isOptSelected } = useSelectPopoverContext();
  const [shouldFlash] = useShouldFlash(idx);
  const [isOverflowing, handleOverflowRef] = useIsOverflow();

  const isSelected = isOptSelected(opt);

  return (
    <OptionWithTooltipWhenOverflowing
      isOverflowing={isOverflowing}
      tooltipProps={{ content: opt.label }}
    >
      <li
        key={opt.value}
        className={cx(
          "option",
          isSelected && "active",
          shouldFlash && "flash",
          !shouldFlash && "flash-enabled"
        )}
        role="option"
        aria-label={`Select option ${opt.label}`}
        aria-selected={isSelected}
        tabIndex={0}
        onClick={() => setValue(opt.value)}
        onKeyDown={(evt) =>
          handleKeyDown({
            evt,
            actionHandlerMap: {
              Enter: () => setValue(opt.value),
              ArrowUp: () => handleOptionKeyDownArrowUp(evt, idx + 1),
              ArrowDown: () => handleOptionKeyDownArrowDown(evt, idx + 1),
            },
          })
        }
        ref={handleOverflowRef}
      >
        {opt.slotLeft?.({
          iconProps: { size: 16, style: { marginRight: 4 } },
        }) ?? null}
        <p className={cx("label-truncation-wrapper")}>{opt.label}</p>
        {opt.slotRight?.({
          iconProps: { size: 16, style: { marginLeft: 4 } },
        }) ?? null}
      </li>
    </OptionWithTooltipWhenOverflowing>
  );
};

const OptionWithTooltipWhenOverflowing = ({
  isOverflowing,
  tooltipProps,
  children,
}: PropsWithChildren<{
  isOverflowing: boolean;
  tooltipProps: TooltipProps;
}>) =>
  isOverflowing ? (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={cx("tooltip-content")}
            sideOffset={5}
            side="left"
          >
            {tooltipProps.content}
            <Tooltip.Arrow className={cx("tooltip-arrow")} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  ) : (
    <>{children}</>
  );

const SelectPopoverContext = createContext<SelectPopoverContextType | null>(
  null
);

export const useSelectPopoverContext = () => {
  const ctx = useContext(SelectPopoverContext);

  if (ctx === null) {
    throw new Error("Must be used within a SelectPopoverContextProvider.");
  }

  return ctx;
};

export const SelectPopoverContextProvider = ({
  children,
  ...restProps
}: PropsWithChildren<SelectPopoverType>) => {
  const {
    defaultValue,
    isMulti,
    onChange,
    options,
    allowOptionPinning,
    value: providedValue,
  } = restProps;

  // because options can be pinned or unpinned within the component, we need to
  // maintain an internal state of the options
  const [modifiableOptions, setModifiableOptions] = useState(() =>
    allowOptionPinning
      ? sortOptionsByPinned(options?.slice() ?? [])
      : (options ?? []).slice().map((opt) => ({ ...opt, isPinned: false }))
  );
  const selectableOptions = useMemo(
    () => getSelectableOptions(modifiableOptions),
    [modifiableOptions]
  );
  const [value, setValue] = useState(defaultValue ?? []);
  const [search, setSearch] = useState("");
  const id = useRef(nanoid());

  // narrow by search only if there is a search term
  const searchNarrowedOptions = useMemo(
    () =>
      search ? searchForOpt(search, modifiableOptions) : modifiableOptions,
    [search, modifiableOptions]
  );

  // if the option is not the first and (option pinning is off or (option pinning
  // is on and the previous option is pinned while current is also pinned) or (option
  // pinning is on and the previous option is unpinned while current is also
  // unpinned)) heavily depends on the fact that pinned options always appear at the top when sorting
  const canMoveOptionUp = (optIdx: number, opts: SelectPopoverOptionType[]) =>
    Boolean(
      optIdx !== 0 &&
        (!allowOptionPinning ||
          (allowOptionPinning &&
            !opts[optIdx].isPinned &&
            opts[optIdx - 1] &&
            !opts[optIdx - 1].isPinned) ||
          (allowOptionPinning &&
            opts[optIdx].isPinned &&
            opts[optIdx - 1] &&
            opts[optIdx - 1].isPinned))
    );
  // if the option is not the last and (option pinning is off or (option pinning
  // is on and the next option is pinned while current is also pinned) or (option
  // pinning is on and the next option is unpinned while current is also
  // unpinned)) heavily depends on the fact that pinned options always appear at the top when sorting
  const canMoveOptionDown = (optIdx: number, opts: SelectPopoverOptionType[]) =>
    Boolean(
      optIdx !== opts.length - 1 &&
        (!allowOptionPinning ||
          (allowOptionPinning &&
            opts[optIdx].isPinned &&
            opts[optIdx + 1] &&
            opts[optIdx + 1].isPinned) ||
          (allowOptionPinning &&
            !opts[optIdx].isPinned &&
            opts[optIdx + 1] &&
            !opts[optIdx + 1].isPinned))
    );

  const isOptSelected = (opt: SelectPopoverOptionType): boolean => {
    if (!opt.options) {
      return value.includes(opt.value);
    }

    return opt.options.every((o) => isOptSelected(o));
  };

  // if option pinning is toggled off, all options should be unpinned or ordering won't work correctly
  useEffect(() => {
    if (allowOptionPinning) {
      return;
    }

    setModifiableOptions((currentOpts) =>
      currentOpts.map((opt) => ({ ...opt, isPinned: false }))
    );
  }, [allowOptionPinning]);

  useDeepCompareEffect(() => {
    if (!options) {
      return;
    }

    setModifiableOptions(() => sortOptionsByPinned(options));
  }, [options]);

  // sync provided value to internal value
  useEffect(() => {
    if (!providedValue || isEqual(providedValue, value)) {
      return;
    }

    setValue(providedValue ?? []);
  }, [providedValue, defaultValue]);

  return (
    <SelectPopoverContext.Provider
      value={{
        ...restProps,
        id: id.current,
        value,
        options: modifiableOptions,
        selectableOptions,
        search: {
          value: search,
          onChange: (newSearchValue) => {
            setSearch(newSearchValue);
          },
          options: searchNarrowedOptions,
        },
        setValue: (newValue) => {
          if (isMulti) {
            if (!value.includes(newValue)) {
              const slatedNewValue = getMappedSelectableOptions(
                modifiableOptions,
                (o) => value.includes(o.value) || o.value === newValue
              );

              setValue(slatedNewValue);
              onChange?.(slatedNewValue, modifiableOptions);
            } else {
              const filteredNewValue = getMappedSelectableOptions(
                modifiableOptions,
                (o) => value.includes(o.value) && o.value !== newValue
              );

              setValue(filteredNewValue);
              onChange?.(filteredNewValue, modifiableOptions);
            }
          } else {
            setValue([newValue]);
            onChange?.([newValue], modifiableOptions);
          }
        },
        isOptSelected,
        chooseAllOptions: () => {
          const slatedNewValue = value.length
            ? []
            : getMappedSelectableOptions(searchNarrowedOptions ?? []);

          setValue(slatedNewValue);
          onChange?.(slatedNewValue, modifiableOptions);
        },
        chooseAllOptionsOfGroup: (optGroup) => {
          const groupOptionValues = getMappedSelectableOptions(
            optGroup.options
          );
          const slatedNewValue = value.length
            ? groupOptionValues.some((v) => value.includes(v))
              ? value.filter((v) => !groupOptionValues.includes(v))
              : getMappedSelectableOptions(
                  searchNarrowedOptions ?? [],
                  (o) =>
                    value.includes(o.value) ||
                    groupOptionValues.includes(o.value)
                )
            : groupOptionValues;

          setValue(slatedNewValue);
          onChange?.(slatedNewValue, modifiableOptions);
        },
        pinOption: (optToPin) => {
          const sortedNewOpts = sortOptionsByPinned(
            pinOption(optToPin, modifiableOptions)
          );
          const newValues = getMappedSelectableOptions(sortedNewOpts, (o) =>
            value.includes(o.value)
          );

          setValue(newValues);
          onChange?.(newValues, sortedNewOpts);
          setModifiableOptions(sortedNewOpts);
        },
        canMoveOptionUp,
        canMoveOptionDown,
        moveOptionUp: (optToMove) => {
          const newOpts = moveOptionUp(optToMove, modifiableOptions);
          const newSortedOpts = sortOptionsByPinned(newOpts);
          const newValues = getMappedSelectableOptions(newSortedOpts, (o) =>
            value.includes(o.value)
          );

          setValue(newValues);
          onChange?.(newValues, newSortedOpts);
          setModifiableOptions(newSortedOpts);
        },
        moveOptionDown: (optToMove) => {
          const newOpts = moveOptionDown(optToMove, modifiableOptions);
          const newSortedOpts = sortOptionsByPinned(newOpts);
          const newValues = getMappedSelectableOptions(newSortedOpts, (o) =>
            value.includes(o.value)
          );

          setValue(newValues);
          onChange?.(newValues, newSortedOpts);
          setModifiableOptions(newSortedOpts);
        },
      }}
    >
      {children}
    </SelectPopoverContext.Provider>
  );
};

const SelectPopoverWithContext = ({
  children,
  ...restProps
}: PropsWithChildren<SelectPopoverType>) => (
  <SelectPopoverContextProvider {...restProps}>
    <SelectPopover>{children}</SelectPopover>
  </SelectPopoverContextProvider>
);

export default SelectPopoverWithContext;
